import React, { useState } from 'react';
import { T } from '../shared/constants';
import { ItemFrame, Instruction, CheckButton, ResultCard, partColors } from './ItemChrome';
import { buildStudentAnswer, isCorrectResponse, feedbackFor } from './naplanItems';

/** `hottext` — tap the word/phrase/sentence in a text. Response: sorted segment indices. */
export default function HotTextRenderer({ question, studentAnswer, state, showExplanation, onSubmit, onAskLumi, onExplainVideo }) {
  const isAnswered = studentAnswer != null;
  const segments = question.segments || [];
  const answers = question.answers || [];
  const single = !question.select || question.select === 1;
  const [draft, setDraft] = useState([]);
  const chosen = isAnswered && Array.isArray(studentAnswer.rawAnswer) ? studentAnswer.rawAnswer : draft;
  const isCorrect = isAnswered ? (typeof studentAnswer.isCorrect === 'boolean' ? studentAnswer.isCorrect : isCorrectResponse(question, chosen)) : false;

  function toggle(i) {
    if (isAnswered) return;
    setDraft(d => {
      if (d.includes(i)) return d.filter(x => x !== i);
      return single ? [i] : [...d, i].sort((a, b) => a - b);
    });
  }

  function submit() {
    if (isAnswered || draft.length === 0) return;
    onSubmit(buildStudentAnswer(question, [...draft].sort((a, b) => a - b)));
  }

  return (
    <ItemFrame question={question} state={state} isAnswered={isAnswered}>
      <Instruction>{single ? 'Tap the part of the text that answers the question' : 'Tap every part of the text that answers the question'}</Instruction>
      <div role="group" aria-label="Text to choose from" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 20, fontSize: 16, lineHeight: 2.1, color: T.text }}>
        {segments.map((seg, i) => {
          const on = chosen.includes(i);
          let status = on ? 'selected' : null;
          if (isAnswered) {
            const isAns = answers.includes(i);
            status = isAns && on ? 'right' : on ? 'wrong' : isAns ? 'missed' : null;
          }
          const c = partColors(status);
          const text = String(seg);
          const needsSpace = i < segments.length - 1 && !/\s$/.test(text) && !/^\s/.test(String(segments[i + 1]));
          return (
            <React.Fragment key={i}>
              <button
                type="button"
                aria-pressed={on}
                aria-disabled={isAnswered}
                data-testid={`hot-${i}`}
                data-status={status || 'none'}
                onClick={() => toggle(i)}
                style={{
                  display: "inline", font: "inherit", lineHeight: "inherit", textAlign: "left",
                  background: status ? c.bg : "transparent",
                  border: "none", borderBottom: `2px ${status === 'missed' ? 'dashed' : 'solid'} ${status ? c.border : T.borderHi}`,
                  borderRadius: 4, padding: "1px 2px", margin: 0, color: status ? c.color : T.text,
                  fontWeight: status ? 700 : 400, cursor: isAnswered ? "default" : "pointer",
                  boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone",
                }}
              >
                {isAnswered && status === 'right' && <span aria-hidden="true">✓ </span>}
                {isAnswered && status === 'wrong' && <span aria-hidden="true">✗ </span>}
                {text}
              </button>
              {needsSpace ? ' ' : null}
            </React.Fragment>
          );
        })}
      </div>
      {!isAnswered && <CheckButton mode={state.mode} disabled={draft.length === 0} onClick={submit} />}
      <ResultCard
        question={question} mode={state.mode} isAnswered={isAnswered} isCorrect={isCorrect}
        showExplanation={showExplanation} feedback={isAnswered && !isCorrect ? feedbackFor(question, chosen) : null}
        onAskLumi={onAskLumi} onExplainVideo={onExplainVideo}
      />
    </ItemFrame>
  );
}
